import PropTypes from 'prop-types';
import React, { Fragment, useEffect } from 'react';
import { bindActionCreators, compose } from 'redux';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import { Link, withRouter } from 'react-router-dom';
import Linkify from 'react-linkify';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import LinearProgress from '@material-ui/core/LinearProgress';
import LinkIcon from '@mui/icons-material/Link';
import LockIcon from '@mui/icons-material/Lock';
import VpnLockIcon from '@mui/icons-material/VpnLock';
import { Tooltip } from '@material-ui/core';

import config from 'config';
import { primaryBlue, orange, pink, purple } from 'utils/color-styles';
import Img from 'retired/Img';
import Box from 'retired/Box';
import DialogButton from 'retired/DialogButton';
import ExternalLinkIcon from 'assets/icons/external-link.svg';
import ExternalLinkBlackIcon from 'assets/icons/external-link-black.svg';
import { ViewInAppButton } from 'components/ViewInAppButton';
import Api from 'entities/api';
import { selectDeedById } from 'entities/deed/selectors';
import { selectCampaigns } from 'entities/campaign/selectors';
import { selectCauses } from 'entities/cause/selectors';
import { selectSkills } from 'entities/skill/selectors';
import { selectOrganization, selectUser, selectUserLocations } from 'entities/user/selectors';
import { selectVolunteerTimesByDeed } from 'entities/volunteerTime/selectors';
import { useInjectReducer } from 'utils/injectReducer';
import { useInjectEpics } from 'utils/injectEpics';
import { currencyFormat, currenciesFormat } from 'utils/currencyFormat';
import { getAppDomain } from 'utils/getAppDomain';
import CheckCircleIcon from 'assets/icons/check-circle.svg';
import { Privileges, DeedManagerPrivileges } from 'utils/privileges';
import { RenderHtml } from 'components/RenderHtml';
import { DeedStatus, OpportunityProvider } from 'generated/graphql';

import { AttendeesTable } from './AttendeesTable';
import * as Actions from './actions';
import {
  selectLoading,
  selectEmailAttendeesOverlayVisible,
  selectAddAttendeesOverlayVisible,
  selectInviteAttendeesOverlayVisible,
  selectFormResponsesOverlayUserId,
  selectFormAggregationsOverlayVisible,
  selectError,
} from './selectors';
import FormAggregationsOverlay from './FormAggregationsOverlay';
import FormResponsesOverlay from './FormResponsesOverlay';
import EmailAttendeesForm from './EmailAttendeesForm';
import InviteAttendeesOverlay from './InviteAttendeesOverlay';
import AddAttendeesOverlay from './AddAttendeesOverlay';
import emailAttendeesFormEpics from './EmailAttendeesForm/epics';
import inviteAttendeesOverlayEpics from './InviteAttendeesOverlay/epics';
import inviteAttendeesOverlayReducer from './InviteAttendeesOverlay/reducer';
import addAttendeesOverlayEpics from './AddAttendeesOverlay/epics';
import addAttendeesOverlayReducer from './AddAttendeesOverlay/reducer';
import epics from './epics';
import reducer from './reducer';
import { OptInButton } from './OptInButton';
import { DeedDonorTable } from './DeedDonorTable';
import { DeedDetails } from './DeedDetails';
import { RolesAttendeesTable } from './RolesAttendeesTable';
import { WaitlistTable } from './WaitlistTable';

const categoryStyles = {
  backgroundColor: primaryBlue,
  marginLeft: 3,
  marginBottom: 3,
  color: '#fff',
  opacity: 0.5,
  height: 20,
  padding: '0 15px',
  lineHeight: '20px',
  float: 'left',
  fontWeight: 500,
  fontSize: 10,
};

const DeedPage = ({
  loading,
  emailAttendeesOverlayVisible,
  addAttendeesOverlayVisible,
  inviteAttendeesOverlayVisible,
  formResponsesOverlayUserId,
  formAggregationsOverlayVisible,
  deed,
  campaigns,
  causes,
  skills,
  volunteerTimes,
  organization,
  user,
  history,
  match,
  actions,
  locations,
  error,
}) => {
  const regularDeedAccess =
    deed?.organization?.id === user?.organization?.id ||
    deed?.partner?.id === user?.organization?.id;
  const subsidiaryDeedAccess = !regularDeedAccess && deed?.isSharedWithSubsidiary;
  const optedIn = deed?.optedIn;
  useEffect(() => {
    if (
      // @NOTE-RT: If the user isn't the owner or a partner of the deed, show 404 page
      (deed && user && !regularDeedAccess && !subsidiaryDeedAccess) ||
      // @NOTE-RT: If there's no deed and you're not loading any more, and there is an error, just assume it errored out loading the deed (and that you can't see it)
      (!deed && !loading && error)
    ) {
      history.replace('/notfound');
    }
    if (deed && user && !user.hasPrivilege(DeedManagerPrivileges[deed.type])) {
      history.replace('/');
    }
    if (user?.hasPrivilege(Privileges.VOLUNTEERTIME)) {
      actions.getVolunteerTimes(match.params.id);
    }
    if (deed && deed.pathSlug !== match.params.type) {
      history.replace(`/deeds/${deed.pathSlug}/${deed.id}`);
    }
  }, [user, deed, error, regularDeedAccess, subsidiaryDeedAccess]);
  useInjectReducer({ key: 'deedPage', reducer });
  useInjectEpics({ key: 'deedPage', epics });
  useInjectReducer({
    key: 'deedPage/addAttendeesOverlay',
    reducer: addAttendeesOverlayReducer,
  });
  useInjectEpics({
    key: 'deedPage/addAttendeesOverlay',
    epics: addAttendeesOverlayEpics,
  });
  useInjectReducer({
    key: 'deedPage/inviteAttendeesOverlay',
    reducer: inviteAttendeesOverlayReducer,
  });
  useInjectEpics({
    key: 'deedPage/inviteAttendeesOverlay',
    epics: inviteAttendeesOverlayEpics,
  });
  useInjectEpics({
    key: 'deedPage/emailAttendeesForm',
    epics: emailAttendeesFormEpics,
  });
  useEffect(() => {
    actions.initAction(match.params.id);
  }, []);

  const formatLink = (link) => (link.indexOf('http') === 0 ? link : `http://${link}`);

  const isGoodera = deed?.provider === OpportunityProvider.Goodera;

  const isAdmin = user?.type === 'Admin';

  const isAllowedToEditDeed =
    deed &&
    user.hasPrivilege(Privileges.DEEDS_MANAGE) &&
    user.hasPrivilege(DeedManagerPrivileges[deed.type]) &&
    (deed.manual ||
      !deed.ended ||
      deed.status !== DeedStatus.Published ||
      user.hasPrivilege(Privileges.DEED_EDIT_PAST));

  const editButton = (isAllowedToEditDeed || isAdmin) && (
    <Button
      color="primary"
      variant="contained"
      onClick={() => history.push(`/deeds/${deed.pathSlug}/edit/${deed.id}`)}
    >
      Edit Details
    </Button>
  );

  const duplicateButton = regularDeedAccess && deed && !isGoodera && (
    <Link
      to={`/deeds/${deed.pathSlug}/edit/${deed.id}?duplicate=1`}
      style={{
        textDecorationLine: 'underline',
        padding: 20,
        textUnderlinePosition: 'under',
      }}
    >
      Duplicate Deed
    </Link>
  );

  const viewInAppLink = (regularDeedAccess || optedIn) && deed && (
    <div
      style={{
        display: 'inline-flex',
        marginLeft: 'auto',
        fontSize: 15,
        padding: 20,
        textDecorationLine: 'underline',
        textUnderlinePosition: 'under',
      }}
    >
      <ViewInAppButton
        href={`${getAppDomain([deed, organization, user])}/deeds/${deed.id}`}
        text="View in App"
        disabled={deed.status !== 'published'}
      />
    </div>
  );
  const goalAmountUserCurrency = deed?.goalAmountCurrencies[user?.displayCurrency] || 0;
  const descriptionDiv = deed && (
    <div>
      {organization && !organization.isCompany() && deed.partner ? (
        <div>
          <Typography type="headline" component="h3" style={{ fontSize: 15, fontWeight: 500 }}>
            Partner
          </Typography>
          <Typography component="p" style={{ fontSize: 15, fontWeight: 300 }}>
            {deed.partner.name}
          </Typography>
          <br />
        </div>
      ) : null}

      {deed.externalLink ? (
        <div>
          <Typography type="headline" component="h3" style={{ fontSize: 15, fontWeight: 500 }}>
            External link (hosted outside of Deed)
          </Typography>
          <Typography component="p" style={{ fontSize: 15, fontWeight: 300 }}>
            <a href={formatLink(deed.externalLink)}>
              {formatLink(deed.externalLink)}{' '}
              <Img
                src={ExternalLinkIcon}
                alt="External Link Icon"
                style={{
                  width: 16,
                  height: 16,
                  marginLeft: 5,
                }}
              />
            </a>
          </Typography>
          <br />
        </div>
      ) : null}

      {deed.duties ? (
        <div>
          <Typography type="headline" component="h3" style={{ fontSize: 14, fontWeight: 500 }}>
            Duties
          </Typography>
          <Typography component="p" style={{ fontSize: 15, fontWeight: 300 }}>
            <Linkify>
              {deed.duties.split('\n').map((item, key) => (
                <span key={item || key}>
                  {item}
                  <br />
                </span>
              ))}
            </Linkify>
          </Typography>
          <br />
        </div>
      ) : null}
      {deed.mustKnows ? (
        <div>
          <Typography type="headline" component="h3" style={{ fontSize: 14, fontWeight: 500 }}>
            Must Knows
          </Typography>
          <Typography component="p" style={{ fontSize: 15, fontWeight: 300 }}>
            <Linkify>
              {deed.mustKnows.split('\n').map((item, key) => (
                <span key={item || key}>
                  {item}
                  <br />
                </span>
              ))}
            </Linkify>
          </Typography>
          <br />
        </div>
      ) : null}
      {deed.formQuestions?.size > 0 ? (
        <div>
          <Typography type="headline" component="h3" style={{ fontSize: 14, fontWeight: 500 }}>
            Additional information
          </Typography>
          <ul style={{ paddingLeft: 30, marginBottom: 0 }}>
            {deed.formQuestions.map((formQuestion) => (
              <li key={formQuestion.get('_id')}>
                {formQuestion.get('title')}
                {formQuestion.get('isRequired') ? ' *' : ''}
              </li>
            ))}
          </ul>
          <br />
        </div>
      ) : null}
      {deed.forms?.size > 0 ? (
        <div>
          <Typography type="headline" component="h3" style={{ fontSize: 14, fontWeight: 500 }}>
            Forms
          </Typography>
          <ul style={{ paddingLeft: 30, marginBottom: 0 }}>
            {deed.forms.map((form) => (
              <li key={form.id}>
                <Link to={`/forms/${form.id}`}>{form.name}</Link>
                {form.subtitle ? ` (${form.subtitle})` : ''}
              </li>
            ))}
          </ul>
          <br />
        </div>
      ) : null}
      {deed.organization &&
      deed.organization.id !== user.organization.id &&
      deed.organization.brief ? (
        <div>
          <Typography type="headline" component="h3" style={{ fontSize: 14, fontWeight: 500 }}>
            Organization
          </Typography>
          <Typography component="p" style={{ fontSize: 15, fontWeight: 300 }}>
            <Linkify>
              {deed.organization.brief.split('\n').map((item, key) => (
                <span key={item || key}>
                  {item}
                  <br />
                </span>
              ))}
            </Linkify>
          </Typography>
          <br />
        </div>
      ) : null}
      {deed.description && (
        <div>
          <Typography type="headline" component="h3" style={{ fontSize: 14, fontWeight: 500 }}>
            Description
          </Typography>
          <RenderHtml source={{ html: deed.description || '' }} />
        </div>
      )}
      {(deed.donationText || deed.donationLink) && (
        <Typography
          type="headline"
          component="h3"
          style={{ fontSize: 14, fontWeight: 500, marginTop: 24 }}
        >
          Donation Required
        </Typography>
      )}
      {(deed.donationText || deed.donationLink) && (
        <Typography component="p" style={{ fontSize: 15, fontWeight: 300 }}>
          {deed.donationText}
          {deed.donationText && deed.donationLink && <br />}
          {deed.donationLink && (
            <a href={formatLink(deed.donationLink)}>{formatLink(deed.donationLink)}</a>
          )}
        </Typography>
      )}
      {deed.backgroundCheckLink && (
        <Typography
          type="headline"
          component="h3"
          style={{ fontSize: 14, fontWeight: 500, marginTop: 24 }}
        >
          Background Check
        </Typography>
      )}
      {deed.backgroundCheckLink && (
        <Typography component="p" style={{ fontSize: 15, fontWeight: 300 }}>
          <a href={formatLink(deed.backgroundCheckLink)}>{formatLink(deed.backgroundCheckLink)}</a>
        </Typography>
      )}
      {deed.volunteerFormLink && (
        <Typography
          type="headline"
          component="h3"
          style={{ fontSize: 14, fontWeight: 500, marginTop: 24 }}
        >
          Volunteer Form
        </Typography>
      )}
      {deed.volunteerFormLink && (
        <Typography component="p" style={{ fontSize: 15, fontWeight: 300 }}>
          <a href={formatLink(deed.volunteerFormLink)}>{formatLink(deed.volunteerFormLink)}</a>
        </Typography>
      )}

      {deed.type === 'Campaign' && goalAmountUserCurrency && (
        <>
          <Typography
            type="headline"
            component="h3"
            style={{ fontSize: 14, fontWeight: 500, marginTop: 24 }}
          >
            Goal Amount
          </Typography>
          <Typography component="p" style={{ fontSize: 15, fontWeight: 300 }}>
            {currenciesFormat(deed.goalAmountCurrencies, user.displayCurrency)}
            {deed.currencyCode !== user.displayCurrency
              ? `* (${currenciesFormat(
                  deed.goalAmountCurrencies,
                  deed.currencyCode
                )} in base currency)`
              : ''}
          </Typography>
        </>
      )}

      {deed.donationMatchPercentage && (
        <>
          <Typography
            type="headline"
            component="h3"
            style={{ fontSize: 14, fontWeight: 500, marginTop: 24 }}
          >
            Donation Match
          </Typography>
          <Typography component="p" style={{ fontSize: 15, fontWeight: 300 }}>
            {deed.donationMatchPercentage.toLocaleString('en')}%
          </Typography>
        </>
      )}

      {deed.donationMatchLimit && (
        <>
          <Typography
            type="headline"
            component="h3"
            style={{ fontSize: 14, fontWeight: 500, marginTop: 24 }}
          >
            Donation Match Limit
          </Typography>
          <Typography component="p" style={{ fontSize: 15, fontWeight: 300 }}>
            {currencyFormat(deed.donationMatchLimit, deed.currencyCode)}
          </Typography>
        </>
      )}
    </div>
  );

  const categoryTags = deed && (
    <div style={{ marginTop: -8 }}>
      {deed.categories.map((categoryId) => {
        const category = causes.get(categoryId);
        return (
          category && (
            <span key={categoryId} style={categoryStyles}>
              {category.name}
            </span>
          )
        );
      })}
      {deed.ERGs.map((ERGId) => {
        const ERG = causes.get(ERGId);
        return (
          ERG && (
            <span
              key={ERGId}
              style={{
                ...categoryStyles,
                backgroundColor: pink,
              }}
            >
              {ERG.name}
            </span>
          )
        );
      })}
      {deed.pillars.map((pillarId) => {
        const pillar = causes.get(pillarId);
        return (
          pillar && (
            <span
              key={pillarId}
              style={{
                ...categoryStyles,
                backgroundColor: purple,
              }}
            >
              {pillar.name}
            </span>
          )
        );
      })}
      {deed.campaigns.map((campaignId) => {
        const campaign = campaigns.get(campaignId);
        return (
          campaign && (
            <span
              key={campaignId}
              style={{
                ...categoryStyles,
                backgroundColor: orange,
              }}
            >
              {campaign.title}
            </span>
          )
        );
      })}
      <br />
      {deed.SDGs.map((SDGId) => causes.get(SDGId))
        .sort((a, b) => (b && b.number) - (a && a.number))
        .map(
          (SDG) =>
            SDG && (
              <span
                key={SDG.id}
                style={{
                  ...categoryStyles,
                  backgroundColor: SDG.color,
                }}
              >
                #{SDG && SDG.number} {SDG && SDG.name}
              </span>
            )
        )}
      {deed.SDGs.size > 0 && (
        <>
          <br />
          <br />
        </>
      )}
    </div>
  );

  const cancelButton = regularDeedAccess &&
    !isGoodera &&
    deed &&
    (!deed.ended || deed.manual || user?.isAdmin) && (
      <DialogButton
        style={{
          display: 'inline-block',
          padding: 20,
          textDecorationLine: 'underline',
          textUnderlinePosition: 'under',
        }}
        asLink
        buttonLabel={`${!deed.ended || deed.manual ? 'Remove' : 'Cancel'} Deed`}
        title={`${!deed.ended || deed.manual ? 'Remove' : 'Cancel'} Deed`}
        text={
          !deed.ended || deed.manual || deed.type === 'Campaign'
            ? 'Are you sure you want to remove this Deed?'
            : [
                'Are you sure you want to cancel this Deed? Attending volunteers will be notified automatically.',
                <br key="br1" />,
                <br key="br2" />,
                'If you wish to inform the volunteers of the reason for the cancellation, please use the "Contact Volunteers" below before cancelling.',
              ]
        }
        confirmLabel={`Confirm ${!deed.ended || deed.manual ? 'Remove' : 'Cancel'}`}
        cancelLabel="Go Back"
        handleConfirm={() => actions.cancelAction(deed)}
      />
    );

  const csvResponsesDownloadBtn =
    regularDeedAccess && (deed?.forms?.size > 0 || deed?.formQuestions?.size > 0) ? (
      // eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/click-events-have-key-events
      <a
        style={{
          outline: 'none',
          textDecorationLine: 'underline',
          textUnderlinePosition: 'under',
        }}
        onClick={(e) => {
          const a = e.target.tagName === 'A' ? e.target : e.target.parentElement;
          if (!a.href) {
            fetch(`${config.apiUrl}/api/deeds/${deed.id}/responsesExport`, {
              method: 'GET',
              headers: {
                Authorization: Api.defaultHeaders.Authorization,
              },
            })
              .then((response) => response.blob())
              .then((blob) => {
                const url = window.URL.createObjectURL(blob);
                a.href = url;
                a.download = `${deed.name}-responses.csv`;
                a.click();
                window.URL.revokeObjectURL(url);
                a.removeAttribute('href');
              });
          }
        }}
        role="button"
        tabIndex="0"
        title="Export form results"
      >
        Export Responses{' '}
      </a>
    ) : null;

  const enableSearch = organization && organization.isCompany();

  const getUserFromAttendeeOrRole = () => {
    if (deed.get('type') === 'Project') {
      return deed.roles
        ?.flatMap?.((role) =>
          role.get('applicants').concat(role.get('approved'), role.get('rejected'))
        )
        ?.toJS()
        ?.find((roleUser) => roleUser.id === formResponsesOverlayUserId);
    }
    return deed.attendees.find((item) => String(item.id) === String(formResponsesOverlayUserId));
  };

  const matchedAmountUserCurrency = deed?.matchedAmountCurrencies[user?.displayCurrency] || 0;

  const differentCurrency = deed?.currencyCode !== user?.displayCurrency;
  const OriginalCurrencyTooltip = differentCurrency ? Tooltip : Fragment;

  const optInButton = subsidiaryDeedAccess && (
    <OptInButton deed={deed} user={user} optedIn={optedIn} />
  );

  const attendees = deed?.attendees || [];
  const roleApplicantIds = deed?.roles
    ?.toJS?.()
    ?.flatMap((role) => role.approved?.map((attendee) => attendee.id) || []);
  const nonRoleAttendees = attendees.filter((attendee) => !roleApplicantIds.includes(attendee.id));

  return (
    <>
      {loading && <LinearProgress />}

      {addAttendeesOverlayVisible && (
        <AddAttendeesOverlay
          visible={addAttendeesOverlayVisible}
          deed={deed}
          enableSearch={enableSearch}
          close={() => actions.toggleAddAttendeesOverlayAction(false)}
        />
      )}

      {inviteAttendeesOverlayVisible && (
        <InviteAttendeesOverlay
          visible={inviteAttendeesOverlayVisible}
          deed={deed}
          enableSearch={enableSearch}
          close={() => actions.toggleInviteAttendeesOverlayAction(false)}
        />
      )}

      {!loading && deed && user && (
        <div>
          <Helmet title={deed.name} />
          {deed.forms && (
            <>
              <FormResponsesOverlay
                formResponsesOverlayUser={getUserFromAttendeeOrRole()}
                formResponses={deed.formResponses}
                formQuestions={deed.formQuestions}
                toggleAction={actions.toggleFormResponsesOverlayAction}
              />
              <FormAggregationsOverlay
                visible={formAggregationsOverlayVisible}
                responsesAggregation={deed.responsesAggregation}
                toggleAction={actions.toggleFormAggregationsOverlayAction}
              />
            </>
          )}
          {deed.attendees && deed.attendees.length > 0 && (
            <EmailAttendeesForm
              open={emailAttendeesOverlayVisible}
              deed={deed}
              toggleAction={actions.toggleEmailAttendeesOverlayAction}
            />
          )}
          {/* *************************************************************************************** */}
          {/* Div contains the deed name and the buttons that show up on the right side of the screen */}
          {/* *************************************************************************************** */}
          <div
            style={{
              display: 'flex',
              flexFlow: 'row wrap',
              align: 'center',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 5,
            }}
          >
            {/* Deed name or title */}

            <Typography
              type="headline"
              component="h2"
              style={{ fontSize: 32, fontWeight: 700, marginTop: 0 }}
            >
              {deed.name}
              {deed.linkOnly && (
                <span title="Link only" style={{ marginLeft: 7 }}>
                  <LinkIcon style={{ fontSize: 24, verticalAlign: 'center' }} />
                </span>
              )}
              {deed.requireAuthentication && (
                <span title="Authentication required" style={{ marginLeft: 7 }}>
                  <LockIcon style={{ fontSize: 24, verticalAlign: 'center' }} />
                </span>
              )}
              {deed.allowedLocations?.size > 0 && (
                <span
                  title={`Allowed locations: ${deed.allowedLocations
                    .map((locationId) => locations?.get(locationId)?.name)
                    .join(', ')}`}
                  style={{ marginLeft: 7 }}
                >
                  <VpnLockIcon style={{ fontSize: 24, verticalAlign: 'center' }} />
                </span>
              )}
              {deed.externalLink && (
                <span title="External" style={{ marginLeft: 7 }}>
                  <Img
                    alt="External Link Icon"
                    src={ExternalLinkBlackIcon}
                    style={{
                      width: 24,
                      height: 24,
                    }}
                  />
                </span>
              )}
            </Typography>

            {/* Various links for managing the deed */}
            <Typography component="div" align="right">
              {csvResponsesDownloadBtn}

              {duplicateButton}

              {cancelButton}

              {viewInAppLink}

              {editButton}

              {optInButton}
            </Typography>
          </div>
          {/* ************************************************ */}
          {/* The 2 main panels you first see are in this Grid */}
          {/* ************************************************ */}
          <div style={{ display: 'flex', flexDirection: 'row', gap: 20 }}>
            {/* ********************************************************************************************************* */}
            {/* Left panel: currently shows a picture, category tags, view description button, and milestone related info */}
            {/* ********************************************************************************************************* */}
            <div style={{ flex: 1 }}>
              <Box style={{ padding: 0, marginBottom: 40 }}>
                {deed.mainPicture && (
                  <Img
                    src={deed.mainPicture}
                    alt=""
                    aspectRatio={16 / 9}
                    style={{
                      borderTopLeftRadius: 10,
                      borderTopRightRadius: 10,
                    }}
                  />
                )}
                <div style={{ padding: 25 }}>
                  {categoryTags}
                  <div style={{ textAlign: 'right', marginTop: 20 }}>
                    <DialogButton
                      text={descriptionDiv}
                      buttonLabel="View Description"
                      confirmLabel="Edit Details"
                      cancelLabel="Back"
                      handleConfirm={() => history.push(`/deeds/${deed.pathSlug}/edit/${deed.id}`)}
                    />
                  </div>
                </div>
              </Box>
              {/* Milestone related info */}
              {deed.type === 'Project' && deed.milestones.size > 0 && (
                <Box style={{ marginTop: 20 }}>
                  <Typography
                    type="headline"
                    component="h3"
                    style={{
                      fontSize: 14,
                      fontWeight: 500,
                      marginTop: 24,
                      marginBottom: 30,
                    }}
                  >
                    Milestones
                  </Typography>
                  {deed.milestones.toArray().map((milestone, index) => (
                    <div style={{ marginBottom: 30 }} key={milestone.get('_id')}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          flexGrow: 1,
                        }}
                      >
                        <Typography
                          component="p"
                          style={{
                            fontSize: 15,
                            fontWeight: 500,
                            marginBottom: 15,
                          }}
                        >
                          #{index + 1} {milestone.get('name')}
                        </Typography>
                        {milestone.get('completed') ? (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              marginLeft: 'auto',
                            }}
                          >
                            <Img
                              src={CheckCircleIcon}
                              style={{ width: '20px', marginRight: 6 }}
                              alt=""
                            />
                            <span style={{ fontSize: 15 }}>Completed</span>
                          </div>
                        ) : (
                          <DialogButton
                            style={{
                              marginLeft: 'auto',
                              fontSize: 12,
                            }}
                            buttonLabel="Complete"
                            buttonProps={{ color: 'primary' }}
                            title="Complete this Milestone?"
                            text="Once you have completed this milestone, you won&rsquo;t be able to undo it."
                            confirmLabel="Complete"
                            cancelLabel="Cancel"
                            handleConfirm={() =>
                              actions.completeMilestoneAction(deed.id, milestone.get('_id'))
                            }
                          />
                        )}
                      </div>
                      {milestone.get('description') && (
                        <Typography
                          component="p"
                          style={{
                            fontSize: 15,
                            fontWeight: 300,
                            marginTop: 15,
                            overflow: 'hidden',
                            wordWrap: 'break-word',
                          }}
                        >
                          <Linkify>
                            {milestone
                              .get('description')
                              .split('\n')
                              .map((item, key) => (
                                <span key={item || key}>
                                  {item}
                                  <br />
                                </span>
                              ))}
                          </Linkify>
                        </Typography>
                      )}
                    </div>
                  ))}
                </Box>
              )}

              {/* ************************************ */}
              {/* Table showing attendees for the deed */}
              {/* ************************************ */}
              <div style={{ flex: 1 }}>
                {['Event', 'BaseEvent'].includes(deed.type) && nonRoleAttendees.length > 0 && (
                  <AttendeesTable
                    attendees={nonRoleAttendees}
                    deed={deed}
                    organization={organization}
                    volunteerTimes={volunteerTimes}
                    title="Attendees"
                  />
                )}
                {/* **************************************************************** */}
                {/* Box listing roles needed for the deed and progress on the search */}
                {/* **************************************************************** */}
                {deed.roles?.size > 0 && (
                  <RolesAttendeesTable
                    deed={deed}
                    organization={organization}
                    volunteerTimes={volunteerTimes}
                    skills={skills}
                  />
                )}

                {/* **************************************************************** */}
                {/* Box listing the waitlisted users */}
                {/* **************************************************************** */}
                <WaitlistTable deed={deed} />
              </div>
            </div>
            {/** ********************************************************************** */}
            {/* Right panel: Contains various details of interest for the deed manager */}
            {/** ********************************************************************** */}
            <div style={{ flex: 1 }}>
              <DeedDetails
                attendees={attendees}
                goalAmountUserCurrency={goalAmountUserCurrency}
                regularDeedAccess={regularDeedAccess}
                OriginalCurrencyTooltip={OriginalCurrencyTooltip}
                differentCurrency={differentCurrency}
              />
              <DeedDonorTable
                history={history}
                matchedAmountUserCurrency={matchedAmountUserCurrency}
                OriginalCurrencyTooltip={OriginalCurrencyTooltip}
                differentCurrency={differentCurrency}
                regularDeedAccess={regularDeedAccess}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

DeedPage.propTypes = {
  loading: PropTypes.bool,
  emailAttendeesOverlayVisible: PropTypes.bool,
  addAttendeesOverlayVisible: PropTypes.bool,
  inviteAttendeesOverlayVisible: PropTypes.bool,
  formResponsesOverlayUserId: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  formAggregationsOverlayVisible: PropTypes.bool,
  deed: PropTypes.object,
  campaigns: PropTypes.object,
  causes: PropTypes.object,
  skills: PropTypes.object,
  volunteerTimes: PropTypes.object,
  organization: PropTypes.object,
  actions: PropTypes.object,
  locations: PropTypes.object,
  user: PropTypes.object,
  history: PropTypes.object,
  match: PropTypes.object,
  error: PropTypes.object,
};

const withConnect = connect(
  (state, props) => ({
    deed: selectDeedById(state, props.match.params.id),
    user: selectUser(state),
    campaigns: selectCampaigns(state),
    causes: selectCauses(state),
    skills: selectSkills(state),
    volunteerTimes: selectVolunteerTimesByDeed(state, props.match.params.id),
    organization: selectOrganization(state),
    loading: selectLoading(state),
    locations: selectUserLocations(state),
    emailAttendeesOverlayVisible: selectEmailAttendeesOverlayVisible(state),
    addAttendeesOverlayVisible: selectAddAttendeesOverlayVisible(state),
    inviteAttendeesOverlayVisible: selectInviteAttendeesOverlayVisible(state),
    formResponsesOverlayUserId: selectFormResponsesOverlayUserId(state),
    formAggregationsOverlayVisible: selectFormAggregationsOverlayVisible(state),
    error: selectError(state),
  }),
  (dispatch) => ({
    actions: bindActionCreators(Actions, dispatch),
  })
);

export default compose(withRouter, withConnect)(DeedPage);
